import { useState } from "react";
import { Images, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Modal } from "./kit";

const OK_TYPES = ["image/png", "image/jpeg", "image/webp"];
const stem = (name) => name.replace(/\.[^.]+$/, "").trim().toUpperCase();

function MatchList({ rows }) {
  return (
    <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1" data-testid="bulk-photos-list">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center justify-between gap-3 rounded-xl bg-white/[0.03] px-3 py-2 text-sm" data-testid={`bulk-photo-row-${i}`}>
          <span className="truncate font-mono text-xs text-zinc-400">{r.file.name}</span>
          {r.student ? <span className="flex items-center gap-1.5 truncate text-emerald-300"><CheckCircle size={15} weight="fill" />{r.student.name}{r.student.has_photo ? <em className="not-italic text-zinc-500"> · replaces</em> : null}</span>
            : <span className="flex items-center gap-1.5 text-rose-300"><WarningCircle size={15} weight="fill" />{r.reason}</span>}
          {r.status && <span className={`text-xs ${r.status === "saved" ? "text-emerald-300" : "text-rose-300"}`}>{r.status}</span>}
        </div>
      ))}
    </div>
  );
}

export default function BulkPhotos({ open, setOpen, onDone }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const pick = async (files) => {
    if (!files.length) return;
    try {
      const studs = (await api.get("/students")).data;
      const byAdm = Object.fromEntries(studs.map((s) => [String(s.admission_no).toUpperCase(), s]));
      setRows([...files].map((file) => {
        if (!OK_TYPES.includes(file.type)) return { file, reason: "Not a PNG/JPG/WEBP" };
        const student = byAdm[stem(file.name)];
        return student ? { file, student } : { file, reason: "No matching admission no" };
      }));
      setDone(0);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const matched = rows.filter((r) => r.student);
  const save = async () => {
    setBusy(true);
    const queue = [...matched];
    const work = async () => {
      for (let r = queue.shift(); r; r = queue.shift()) {
        const fd = new FormData();
        fd.append("file", r.file);
        try {
          await api.put(`/photos/student/${r.student.id}`, fd);
          r.status = "saved";
        } catch (e) {
          r.status = errMsg(e);
        }
        setDone((d) => d + 1);
        setRows((x) => [...x]);
      }
    };
    await Promise.all([work(), work(), work()]);
    setBusy(false);
    const ok = matched.filter((r) => r.status === "saved").length;
    toast.success(`${ok} photo${ok === 1 ? "" : "s"} saved`);
    onDone();
  };
  const close = (o) => { if (!o && !busy) { setOpen(false); setRows([]); } };
  return (
    <Modal open={open} onOpenChange={close} title="Bulk photo upload" wide testid="bulk-photos-dialog"
      description="Name each file with the student's admission number, e.g. GWD0001.jpg. Existing photos are replaced.">
      <div className="space-y-4">
        <label className="flex cursor-pointer flex-col items-center gap-2 rounded-2xl border border-dashed border-white/15 bg-white/[0.02] px-4 py-8 text-center text-sm text-zinc-400 hover:border-blue-400/50" data-testid="bulk-photos-drop">
          <Images size={30} weight="duotone" className="text-blue-300" />
          Choose photos (you can select many at once)
          <input type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" disabled={busy}
            onChange={(e) => { pick([...e.target.files]); e.target.value = ""; }} data-testid="bulk-photos-input" />
        </label>
        {rows.length > 0 && (
          <>
            <div className="flex flex-wrap gap-4 text-sm" data-testid="bulk-photos-summary">
              <span className="text-emerald-300" data-testid="bulk-photos-matched">{matched.length} matched</span>
              <span className="text-rose-300" data-testid="bulk-photos-unmatched">{rows.length - matched.length} not matched</span>
              {busy || done ? <span className="text-zinc-400" data-testid="bulk-photos-progress">{done}/{matched.length} uploaded</span> : null}
            </div>
            {(busy || done > 0) && <div className="h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full bg-gradient-to-r from-blue-500 to-orange-400 transition-[width]" style={{ width: `${matched.length ? (done / matched.length) * 100 : 0}%` }} /></div>}
            <MatchList rows={rows} />
            <button className="btn-primary w-full" disabled={busy || !matched.length || done === matched.length} onClick={save} data-testid="bulk-photos-save-btn">
              {busy ? "Uploading…" : done === matched.length && done ? "Done" : `Save ${matched.length} photo${matched.length === 1 ? "" : "s"}`}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}
